import React from "react";
import { Box, Flex, Heading, Stack, SkuBot, Text, Button, UpArrow } from "@commonsku/design-system";
import { ChatInput } from "./ChatInput";

export type ChatWelcomeScreenProps = {
  userName: string
  input: string
  onInputChange: (value: string) => void
  onSend: (message?: string) => void
  suggestions?: string[]
  disabled?: boolean
  className?: string
  aboveInput?: React.ReactNode
  dockInput?: boolean
}

const botName = "skubot";
const title = "Hi, I'm {botName}.";
const placeholder = "Ask me anything about your commonsku data...";

export function ChatWelcomeScreen(props: ChatWelcomeScreenProps) {
  const {
    userName,
    input,
    onInputChange,
    onSend,
    suggestions = [],
    disabled = false,
    className,
    aboveInput,
    dockInput = false,
  } = props;

  const welcomeContent = (
    <Stack
      className="skubot-welcome-content"
      w="full"
      maxW="700px"
      align="center"
      gap="5"
      overflow="auto"
    >
      <Box w="100px" h="60px" display="flex" alignItems="center" justifyContent="center">
        <SkuBot color="skuBlue.600" size="2xl" height="100px" width="100px" />
      </Box>

      <Heading as="h1" fontSize="2xl" fontWeight="medium" color="gray.900">
        {title.replace("{userName}", userName).replace("{botName}", botName)}
      </Heading>

      {!dockInput ? (
        <>
          {aboveInput ? <Box w="full">{aboveInput}</Box> : null}
          <ChatInput
            value={input}
            onChange={onInputChange}
            onSubmit={() => onSend()}
            placeholder={placeholder}
            disabled={disabled}
          />
        </>
      ) : null}

      {(suggestions?.length ?? 0) > 0 ? <>
        <Text mt="2" fontFamily="body" fontSize="sm" color="gray.500" mb="0" pb="16px">
          You can ask {botName} about:
        </Text>

        <Stack w="full" gap="0">
          {suggestions.map((suggestion) => (
            <Button
              key={suggestion}
              type="button"
              variant="ghost"
              h="40px"
              justifyContent="space-between"
              px="4"
              borderRadius="0"
              borderBottom="none"
              borderRight="none"
              borderLeft="none"
              borderTop="1px solid var(--chakra-colors-gray-100)" // TODO: replace gray-100 with border-subtle
              color="gray.900"
              _hover={{ bg: "gray.50" }}
              onClick={() => {
                if (disabled) {
                  return;
                }
                onInputChange(suggestion);
              }}
              disabled={disabled}
            >
              <Text fontFamily="body" fontSize="sm" fontWeight="medium" margin="0" padding="0" lineHeight="20px">
                {suggestion}
              </Text>
              <UpArrow size="md" />
            </Button>
          ))}
        </Stack>
      </> : null}
    </Stack>
  );

  if (dockInput) {
    return (
      <Flex
        className={className}
        flex="1"
        minH={0}
        direction="column"
      >
        <Flex
          flex="1"
          minH={0}
          align="center"
          justify="center"
          px={{ base: "5", md: "10" }}
          overflow="auto"
        >
          {welcomeContent}
        </Flex>
        <Box className="skubot-welcome-input-section" w="full" flexShrink={0}>
          <Stack
            w="full"
            maxW="700px"
            mx="auto"
            align="center"
            gap="16px"
          >
            {aboveInput ? <Box w="full">{aboveInput}</Box> : null}
            <ChatInput
              value={input}
              onChange={onInputChange}
              onSubmit={() => onSend()}
              placeholder={placeholder}
              disabled={disabled}
            />
          </Stack>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex
      className={className}
      flex="1"
      align="center"
      justify="center"
      px={{ base: "5", md: "10" }}
      overflow="auto"
    >
      {welcomeContent}
    </Flex>
  );
}
